<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useLotStore } from '../../store/lot.store'
import { useParameterStore } from '../../store/parameter.store'
import { useUsersStore } from '../../store/users.store'
import { useOptions } from '../../store/options.store'
import { useClientOrderRowStore } from '../../store/clientOrderRow.store'
import { useBoars } from '../../store/boars.store'
import { DAILY_PRODUCTION_SCHEMAS_OPTION } from '../../constantes/options-constantes'
import type { IParameter } from '../../models/parameter'
import type { ILot } from '../../models/lot'
import type { IClientOrderRow } from '../../models/clientorderrow'
import type { ILotFilter } from '../../models/filters/lotFilter'
import type { IClientOrderRowFilter } from '../../models/filters/clientOrderRowFilter'

const lotStore = useLotStore()
const parameterStore = useParameterStore()
const userStore = useUsersStore()
const optionStore = useOptions()
const clientOrderRowStore = useClientOrderRowStore()
const boarsStore = useBoars()

const isLoading = ref(false)

// Pour une commande spécifique, l'articleCode contient le nom du verrat (ex: 'ALWIC77').
// On cherche ce verrat dans le store et on remonte son geneticType.code3 (ex: 'ALW').
// Si le verrat n'est pas trouvé, on extrait le code schéma = les 3 premiers caractères de l'articleCode.
function resolveSchema(row: IClientOrderRow): string {
	const articleCode = row.articleCode || ''
	const boar = boarsStore.datas.find(b => b.name === articleCode)
	if (boar?.geneticType?.code3) return boar.geneticType.code3
	return articleCode.substring(0, 3) || articleCode
}

// Schémas configurés depuis les options du centre
const configuredSchemas = computed(() => {
	const schemasOption = optionStore.getOptionByCode(DAILY_PRODUCTION_SCHEMAS_OPTION, userStore.currentUser, userStore.currentCenter)?.value
	if (schemasOption && schemasOption.trim() !== '') {
		return schemasOption.split(',').filter((s: string) => s.trim() !== '')
	}
	return []
})

// Vérifie si des schémas sont configurés
const hasConfiguredSchemas = computed(() => configuredSchemas.value.length > 0)

// Vérifier si les données sont chargées
const hasLotData = computed(() => lotStore.datas.length > 0)

// Charger / actualiser les données à chaque ouverture
onMounted(async () => {
	const centerId = userStore.currentCenter?.id
	if (!centerId) return

	isLoading.value = true
	await Promise.all([
		lotStore.load({ centerId, page: 1, pageSize: 0, include: '*' } as ILotFilter),
		clientOrderRowStore.load({ centerId, page: 1, pageSize: 0, include: '*' } as IClientOrderRowFilter),
	])
	isLoading.value = false
})

// Schémas filtrés selon la configuration (triés alphabétiquement)
const filteredSchemas = computed(() =>
	(parameterStore.getParameterByCodePairActif('ANIMAUX', 'SCHEMA') || [])
		.filter((s: IParameter) => configuredSchemas.value.includes(s.code3 || ''))
		.sort((a: IParameter, b: IParameter) => (a.code3 || '').localeCompare(b.code3 || ''))
)

// Helper pour vérifier si une date est aujourd'hui
function isToday(date: Date | string): boolean {
	const today = new Date()
	const lotDate = new Date(date)
	return (
		lotDate.getDate() === today.getDate() &&
		lotDate.getMonth() === today.getMonth() &&
		lotDate.getFullYear() === today.getFullYear()
	)
}

// Filtrer les lots du jour (monospermie uniquement)
const todayLots = computed(() =>
	lotStore.datas.filter((lot: ILot) => isToday(lot.dateLot) && !lot.isHeterospermie)
)

// Calculer les comptages par schéma
const countBySchema = computed(() => {
	const counts: Record<string, number> = {}
	for (const lot of todayLots.value) {
		const schema = lot.geneticType?.code3 || ''
		counts[schema] = (counts[schema] || 0) + 1
	}
	return counts
})

// Codes des schémas configurés (pour filtrer les totaux)
const configuredSchemaCodes = computed(() => filteredSchemas.value.map(s => s.code3 || ''))

// Total (uniquement les schémas sélectionnés)
const total = computed(() =>
	todayLots.value.filter(lot => configuredSchemaCodes.value.includes(lot.geneticType?.code3 || '')).length
)

// Commandes du jour filtrées (même logique que FabricationDosesTile)
const todayOrderRows = computed(() => {
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	return clientOrderRowStore.datas?.filter(row => {
		if (!row.clientOrder?.dateInvoice) return false
		const date = new Date(row.clientOrder.dateInvoice)
		date.setHours(0, 0, 0, 0)
		return date.getTime() === today.getTime()
			&& row.clientOrder.active
			&& row.active
	}) || []
})

// Comptage commandes par schéma
const orderCountBySchema = computed(() => {
	const counts: Record<string, number> = {}
	for (const row of todayOrderRows.value) {
		const schema = resolveSchema(row)
		counts[schema] = (counts[schema] || 0) + 1
	}
	return counts
})

const totalOrders = computed(() =>
	todayOrderRows.value.filter(row => configuredSchemaCodes.value.includes(resolveSchema(row))).length
)

// Quantité des commandes par schéma (somme des quantity)
const orderQuantityBySchema = computed(() => {
	const quantities: Record<string, number> = {}
	for (const row of todayOrderRows.value) {
		const schema = resolveSchema(row)
		quantities[schema] = (quantities[schema] || 0) + row.quantity
	}
	return quantities
})

const totalOrderQuantity = computed(() =>
	configuredSchemaCodes.value.reduce((sum, code) => sum + (orderQuantityBySchema.value[code] || 0), 0)
)

// Quantité des commandes par schéma ET par conditionnement
// Structure : { schema: { condCode: quantity } }
const orderQuantityBySchemaAndCond = computed(() => {
	const result: Record<string, Record<string, number>> = {}
	for (const row of todayOrderRows.value) {
		const schema = resolveSchema(row)
		const cond = row.conditionnement?.code3 || '?'
		if (!result[schema]) result[schema] = {}
		result[schema][cond] = (result[schema][cond] || 0) + row.quantity
	}
	return result
})

// Total par conditionnement (tous schémas configurés confondus)
const totalOrderQuantityByCond = computed(() => {
	const totals: Record<string, number> = {}
	for (const code of configuredSchemaCodes.value) {
		const conds = orderQuantityBySchemaAndCond.value[code]
		if (!conds) continue
		for (const [cond, qty] of Object.entries(conds)) {
			totals[cond] = (totals[cond] || 0) + qty
		}
	}
	return totals
})

// Ligne de données productions pour el-table
const tableRow = computed(() => {
	const row: Record<string, number> = { label: total.value }
	for (const s of filteredSchemas.value) {
		row[s.code3 || ''] = countBySchema.value[s.code3 || ''] || 0
	}
	return row
})

// Ligne de données commandes pour el-table
const orderTableRow = computed(() => {
	const row: Record<string, number> = { label: totalOrders.value }
	for (const s of filteredSchemas.value) {
		row[s.code3 || ''] = orderCountBySchema.value[s.code3 || ''] || 0
	}
	return row
})

// Ligne de données quantité commandes pour el-table
const orderQuantityTableRow = computed(() => {
	const row: Record<string, number> = { label: totalOrderQuantity.value }
	for (const s of filteredSchemas.value) {
		row[s.code3 || ''] = orderQuantityBySchema.value[s.code3 || ''] || 0
	}
	return row
})

// Ligne de détail par conditionnement pour el-table
const orderDetailTableRow = computed(() => {
	const row: Record<string, any> = {
		label: totalOrderQuantityByCond.value,
		_isDetail: true
	}
	for (const s of filteredSchemas.value) {
		row[s.code3 || ''] = orderQuantityBySchemaAndCond.value[s.code3 || ''] || {}
	}
	return row
})

function condEntries(condMap: Record<string, number>): [string, number][] {
	return Object.entries(condMap).sort(([a], [b]) => a.localeCompare(b))
}

// Obtenir tous les conditionnements uniques utilisés
const allConditionnements = computed(() => {
	const conds = new Set<string>()
	for (const row of todayOrderRows.value) {
		const cond = row.conditionnement?.code3 || '?'
		conds.add(cond)
	}
	return Array.from(conds).sort()
})

const rowLabels = ['Nombre de verrats prelevés', 'Nombre de commandes', 'Quantité des commandes', 'Détail par conditionnement']
</script>

<template>
	<div class="daily-production" v-loading="isLoading">
		<!-- Contenu si données chargées -->
		<template v-if="hasLotData || !isLoading">
			<!-- Message si aucun schéma configuré -->
			<div v-if="!hasConfiguredSchemas" class="no-schemas-message">
				<el-alert
					title="Aucun schéma configuré"
					type="info"
					description="Veuillez configurer les schémas dans Options > Fabrication > Schémas prod. journalière"
					:closable="false"
					show-icon
				/>
			</div>

			<!-- Tableau des productions -->
			<el-table v-else :data="[tableRow, orderTableRow, orderQuantityTableRow, orderDetailTableRow]" border>
				<el-table-column label="" align="left" width="240">
					<template #default="{ $index }">
						{{ rowLabels[$index] }}
					</template>
				</el-table-column>
				<el-table-column label="Total" align="center">
					<template #default="{ row, $index }">
						<div v-if="row._isDetail && $index === 3" class="cell-detail-split-container">
							<div v-for="([cond, qty]) in condEntries(row.label)" :key="cond" class="cell-detail-split-col">
								<div class="cond-header">{{ cond }}</div>
								<div class="cond-value">{{ qty }}</div>
							</div>
						</div>
						<div v-else-if="row._isDetail" class="cell-detail-container">
							<div v-for="([cond, qty]) in condEntries(row.label)" :key="cond" class="cell-detail-row">
								<span class="cond-code">{{ cond }}</span><span class="cond-qty">{{ qty }}</span>
							</div>
						</div>
						<span v-else :class="{ 'cell-negative': row.label < 0 }">{{ row.label }}</span>
					</template>
				</el-table-column>
				<el-table-column v-for="schema in filteredSchemas" :key="schema.id" :label="schema.code3" align="center">
					<template #default="{ row, $index }">
						<div v-if="row._isDetail && $index === 3" class="cell-detail-split-container">
							<div v-for="([cond, qty]) in condEntries(row[schema.code3])" :key="cond" class="cell-detail-split-col">
								<div class="cond-header">{{ cond }}</div>
								<div class="cond-value">{{ qty }}</div>
							</div>
						</div>
						<div v-else-if="row._isDetail" class="cell-detail-container">
							<div v-for="([cond, qty]) in condEntries(row[schema.code3])" :key="cond" class="cell-detail-row">
								<span class="cond-code">{{ cond }}</span><span class="cond-qty">{{ qty }}</span>
							</div>
						</div>
						<span v-else :class="{ 'cell-negative': row[schema.code3] < 0 }">{{ row[schema.code3] }}</span>
					</template>
				</el-table-column>
			</el-table>
		</template>
	</div>
</template>

<style scoped>
.daily-production {
	font-family: "OpenSans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.daily-production :deep(.el-table) {
	font-family: "OpenSans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.daily-production :deep(.el-table th) {
	font-weight: 700;
}

.no-schemas-message {
	padding: 16px 0;
}

.cell-negative {
	color: red;
	font-weight: 700;
}

.cell-detail-container {
	display: flex;
	flex-direction: column;
}

.cell-detail-row {
	display: flex;
	justify-content: center;
	gap: 4px;
	padding: 2px 0;
	font-size: 0.85em;
}

.cell-detail-row + .cell-detail-row {
	border-top: 1px solid #ebeef5;
}

.cond-code {
	font-weight: 700;
}

.cond-qty {
	min-width: 20px;
	text-align: right;
}

.daily-production :deep(.el-table th .cell) {
	color: black;
	font-weight: 700;
}

.cond-header {
	font-weight: 700;
	font-size: 0.9em;
	padding: 2px 0;
}

.cond-value {
	font-weight: 600;
	color: grey;
	padding: 4px 0;
}

.cell-detail-split-container {
	display: flex;
	justify-content: center;
	gap: 8px;
}

.cell-detail-split-col {
	display: flex;
	flex-direction: column;
	align-items: center;
	min-width: 40px;
	border-radius: 4px;
	overflow: hidden;
}
</style>
